import { Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import { Navigate } from '@ngxs/router-plugin';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '../../internal';

export interface SlideAuthorStateModel {
  theme: FlatModels.SlideAuthorModels.MedsurfTheme,
  layout: 'list' | 'grid',
  mediaPanelData: FlatModels.SlideAuthorModels.PanelData,
  chapterPanelData: FlatModels.SlideAuthorModels.PanelData,
  layerPanelData: FlatModels.SlideAuthorModels.PanelData,
  wysiwygPanelData: FlatModels.SlideAuthorModels.PanelData,
  editPanelData: FlatModels.SlideAuthorModels.PanelData,
  typedNodeExpanded: { [id: string]: boolean },
  typedPageExpanded: { [id: string]: boolean },
  typedSlideLayersExpanded: { [id: string]: boolean },
  typedSlideAnnotationsExpanded: { [id: string]: boolean },
  typedSlideGroupsExpanded: { [id: string]: boolean },
  typedSlidePoisExpanded: { [id: string]: boolean },
  typedSlideQuestionsExpanded: { [id: string]: boolean },
  typedLayerExpanded: { [id: string]: boolean },
  typedImageExpanded: { [id: string]: boolean },
  typedImageAnnotationExpanded: { [id: string]: boolean },
  typedImagePoiExpanded: { [id: string]: boolean },
  typedGroupExpanded: { [id: string]: boolean },
  typedPoiExpanded: { [id: string]: boolean },
  typedPoiAnnotationExpanded: { [id: string]: boolean },
  typedQuestionExpanded: { [id: string]: boolean },
  editPanelOverlay: boolean,
  editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay,
  editPanelBulkEdit: boolean,
  mainPanelDisplay: FlatModels.SlideAuthorModels.MainPanelDisplay,
  isSplitterMoving: boolean
}

@State<SlideAuthorStateModel>({
  name: 'slideAuthor',
  defaults: {
    theme: FlatModels.SlideAuthorModels.MedsurfTheme.enum.dark,
    layout: 'list',
    mediaPanelData: {
      isMinimized: true,
      panelSize: FlatModels.PanelDesignModels.mediaPanelSize,
      uncollapsedPanelSize: FlatModels.PanelDesignModels.mediaPanelSize,
    },
    chapterPanelData: {
      isMinimized: false,
      panelSize: FlatModels.PanelDesignModels.chapterPanelSize,
      uncollapsedPanelSize: FlatModels.PanelDesignModels.chapterPanelSize,
    },
    layerPanelData: {
      isMinimized: false,
      panelSize: FlatModels.PanelDesignModels.layerPanelSize,
      uncollapsedPanelSize: FlatModels.PanelDesignModels.layerPanelSize,
    },
    wysiwygPanelData: {
      isMinimized: false,
      panelSize: FlatModels.PanelDesignModels.wysiwygPanelSize,
      uncollapsedPanelSize: FlatModels.PanelDesignModels.wysiwygPanelSize,
    },
    editPanelData: {
      isMinimized: false,
      panelSize: FlatModels.PanelDesignModels.editPanelSize,
      uncollapsedPanelSize: FlatModels.PanelDesignModels.editPanelSize,
    },
    typedNodeExpanded: {},
    typedPageExpanded: {},
    typedSlideLayersExpanded: {},
    typedSlideAnnotationsExpanded: {},
    typedSlideGroupsExpanded: {},
    typedSlidePoisExpanded: {},
    typedSlideQuestionsExpanded: {},
    typedLayerExpanded: {},
    typedImageExpanded: {},
    typedImageAnnotationExpanded: {},
    typedImagePoiExpanded: {},
    typedGroupExpanded: {},
    typedPoiExpanded: {},
    typedPoiAnnotationExpanded: {},
    typedQuestionExpanded: {},
    editPanelOverlay: true,
    editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay.enum.list,
    editPanelBulkEdit: false,
    mainPanelDisplay: FlatModels.SlideAuthorModels.MainPanelDisplay.enum.all,
    isSplitterMoving: false,
  }
})
@Injectable()
export class SlideAuthorState {
  constructor(public store: Store) {
  }

  //<editor-fold desc="Selectors">
  
  @Selector([SlideAuthorState])
  static theme$(state: SlideAuthorStateModel) {
    return state.theme;
  }
  
  @Selector([SlideAuthorState])
  static layout$(state: SlideAuthorStateModel) {
    return state.layout;
  }

  @Selector([SlideAuthorState])
  static mediaPanelData$(state: SlideAuthorStateModel) {
    return structuredClone(state.mediaPanelData);
  }

  @Selector([SlideAuthorState])
  static chapterPanelData$(state: SlideAuthorStateModel) {
    return structuredClone(state.chapterPanelData);
  }

  @Selector([SlideAuthorState])
  static layerPanelData$(state: SlideAuthorStateModel) {
    return structuredClone(state.layerPanelData);
  }

  @Selector([SlideAuthorState])
  static wysiwygPanelData$(state: SlideAuthorStateModel) {
    return structuredClone(state.wysiwygPanelData);
  }

  @Selector([SlideAuthorState])
  static editPanelData$(state: SlideAuthorStateModel) {
    return structuredClone(state.editPanelData);
  }

  @Selector([SlideAuthorState])
  static typedNodeExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedNodeExpanded);
  }

  @Selector([SlideAuthorState])
  static typedPageExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedPageExpanded);
  }

  @Selector([SlideAuthorState])
  static typedSlideLayersExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedSlideLayersExpanded);
  }

  @Selector([SlideAuthorState])
  static typedSlideAnnotationsExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedSlideAnnotationsExpanded);
  }

  @Selector([SlideAuthorState])
  static typedSlideGroupsExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedSlideGroupsExpanded);
  }

  @Selector([SlideAuthorState])
  static typedSlidePoisExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedSlidePoisExpanded);
  }
  
  @Selector([SlideAuthorState])
  static typedSlideQuestionsExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedSlideQuestionsExpanded);
  }

  @Selector([SlideAuthorState])
  static typedLayerExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedLayerExpanded);
  }

  @Selector([SlideAuthorState])
  static typedImageExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedImageExpanded);
  }

  @Selector([SlideAuthorState])
  static typedImageAnnotationExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedImageAnnotationExpanded);
  }

  @Selector([SlideAuthorState])
  static typedImagePoiExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedImagePoiExpanded);
  }

  @Selector([SlideAuthorState])
  static typedGroupExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedGroupExpanded);
  }

  @Selector([SlideAuthorState])
  static typedPoiExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedPoiExpanded);
  }

  @Selector([SlideAuthorState])
  static typedPoiAnnotationExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedPoiAnnotationExpanded);
  }
  
  @Selector([SlideAuthorState])
  static typedQuestionExpanded$(state: SlideAuthorStateModel) {
    return structuredClone(state.typedQuestionExpanded);
  }

  @Selector([FlatStates.NodeEntityState.typedRouteElements$])
  static slideUrlByNode$(typedRouteElements: { [id: string]: FlatModels.NodeEntityModels.RouteElement }) {
    return (slideNode: FlatModels.NodeEntityModels.SlideNode) => {
      if (slideNode && slideNode.parentId) {
        const routeElement = typedRouteElements[slideNode.parentId];
        if (routeElement) {
          return ['/' + routeElement.path + '/' + slideNode.route];
        }
      }
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.parent_node_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.SlideAuthorState.slideUrlByNode$"
      );
    }
  }
  
  @Selector([SlideAuthorState])
  static editPanelOverlay$(state: SlideAuthorStateModel) {
    return state.editPanelOverlay;
  }
  
  @Selector([SlideAuthorState])
  static editPanelDisplay$(state: SlideAuthorStateModel) {
    return state.editPanelDisplay;
  }
  
  @Selector([SlideAuthorState])
  static editPanelBulkEdit$(state: SlideAuthorStateModel) {
    return state.editPanelBulkEdit;
  }
  
  @Selector([SlideAuthorState])
  static mainPanelDisplay$(state: SlideAuthorStateModel) {
    return state.mainPanelDisplay;
  }
  
  @Selector([SlideAuthorState])
  static isSplitterMoving$(state: SlideAuthorStateModel) {
    return state.isSplitterMoving;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">
  
  @Action(FlatActions.SlideAuthorActions.Reset)
  reset({patchState}: StateContext<SlideAuthorStateModel>) {
    patchState({
      typedNodeExpanded: {},
      typedPageExpanded: {},
      typedSlideLayersExpanded: {},
      typedSlideAnnotationsExpanded: {},
      typedSlideGroupsExpanded: {},
      typedSlidePoisExpanded: {},
      typedSlideQuestionsExpanded: {},
      typedLayerExpanded: {},
      typedImageExpanded: {},
      typedImageAnnotationExpanded: {},
      typedImagePoiExpanded: {},
      typedGroupExpanded: {},
      typedPoiExpanded: {},
      typedPoiAnnotationExpanded: {},
      typedQuestionExpanded: {},
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.SetTheme)
  setTheme({patchState}: StateContext<SlideAuthorStateModel>,
           {theme}: FlatActions.SlideAuthorActions.SetTheme) {
    patchState({
      theme
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.SetLayout)
  setLayout({patchState}: StateContext<SlideAuthorStateModel>,
            {layout}: FlatActions.SlideAuthorActions.SetLayout) {
    patchState({
      layout
    });
  }

  @Action(FlatActions.SlideAuthorActions.GoToSlideByNode)
  goToSlideByNode({dispatch}: StateContext<SlideAuthorStateModel>,
                  {slideNode}: FlatActions.SlideAuthorActions.GoToSlideByNode) {
    if (!slideNode) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.node_not_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.SlideAuthorState.goToSlideByNode"
      );
    }

    if (slideNode.parentId) {
      const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
      const routeElement = typedRouteElements[slideNode.parentId];
      if (routeElement) {
        // Navigate to first slide
        return dispatch(new Navigate([routeElement.path + '/' + slideNode.route]));
      }
    }
    
    throw new FlatModels.ErrorControlModels.MedsurfError(
      'notification.parent_node_not_found',
      FlatModels.ErrorControlModels.ErrorLevel.enum.error,
      FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
      "LIBS.FlatStates.SlideAuthorState.goToSlideByNode"
    );
  }

  @Action(FlatActions.SlideAuthorActions.GoToSlideByIndex)
  goToSlideByIndex({dispatch}: StateContext<SlideAuthorStateModel>,
                   {index}: FlatActions.SlideAuthorActions.GoToSlideByIndex) {
    const selectedSlideNodes = this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedSlideNodes$);
    if (selectedSlideNodes.length > 0) {
      const selectedSlideNode = selectedSlideNodes.find(sn => sn.position === index);
      if (selectedSlideNode && selectedSlideNode.parentId) {
        const typedRouteElements = this.store.selectSnapshot(FlatStates.NodeEntityState.typedRouteElements$);
        const routeElement = typedRouteElements[selectedSlideNode.parentId];
        if (routeElement) {
          // Navigate to first slide
          dispatch(new Navigate([routeElement.path + '/' + selectedSlideNode.route]));
        } else {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.parent_node_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatStates.SlideAuthorState.goToSlideByIndex"
          );
        }
      } else {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.node_not_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.SlideAuthorState.goToSlideByIndex"
        );
      }
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.no_slides_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.SlideAuthorState.goToSlideByIndex"
      );
    }
  }

  @Action(FlatActions.SlideAuthorActions.SetMediaPanelSize)
  setMediaPanelSize({getState, patchState}: StateContext<SlideAuthorStateModel>,
                    {sizes}: FlatActions.SlideAuthorActions.SetMediaPanelSize) {
    // Get Media Panel Data
    const mediaPanelData = structuredClone(getState().mediaPanelData);

    // Update Media Panel Data
    mediaPanelData.panelSize = sizes;
    mediaPanelData.uncollapsedPanelSize = sizes;

    // Patch State
    patchState({
      mediaPanelData
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.CollapseMedia)
  collapseMedia({getState, patchState}: StateContext<SlideAuthorStateModel>,
                {isMinimized, override}: FlatActions.SlideAuthorActions.CollapseMedia) {
    // Get Media Panel Data
    const mediaPanelData = structuredClone(getState().mediaPanelData);
    
    // Update Edit Panel Data
    mediaPanelData.isMinimized = isMinimized;
    if (mediaPanelData.isMinimized) {
      mediaPanelData.panelSize = [100 - FlatModels.PanelDesignModels.panelCollapseSize, FlatModels.PanelDesignModels.panelCollapseSize];
    } else {
      if (override) {
        if (mediaPanelData.uncollapsedPanelSize && mediaPanelData.uncollapsedPanelSize[1] && mediaPanelData.uncollapsedPanelSize[1] > FlatModels.PanelDesignModels.panelMinSizeBig) {
          mediaPanelData.panelSize = [100 - mediaPanelData.uncollapsedPanelSize[1], mediaPanelData.uncollapsedPanelSize[1]];
        } else {
          mediaPanelData.panelSize = [100 - FlatModels.PanelDesignModels.panelMinSizeBig, FlatModels.PanelDesignModels.panelMinSizeBig];
          mediaPanelData.uncollapsedPanelSize = [100 - FlatModels.PanelDesignModels.panelMinSizeBig, FlatModels.PanelDesignModels.panelMinSizeBig];
        }
      }
    }
    
    // Patch State
    patchState({
      mediaPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetChapterPanelSize)
  setChapterPanelSize({getState, patchState}: StateContext<SlideAuthorStateModel>,
                      {sizes}: FlatActions.SlideAuthorActions.SetChapterPanelSize) {
    // Get Chapter Panel Data
    const chapterPanelData = structuredClone(getState().chapterPanelData);

    // Update Chapter Panel Data
    chapterPanelData.panelSize = sizes;
    chapterPanelData.uncollapsedPanelSize = sizes;

    // Patch State
    patchState({
      chapterPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.CollapseChapter)
  collapseChapter({getState, patchState}: StateContext<SlideAuthorStateModel>,
                  {isMinimized, override}: FlatActions.SlideAuthorActions.CollapseChapter) {
    // Get Chapter Panel Data
    const chapterPanelData = structuredClone(getState().chapterPanelData);

    // Update Chapter Panel Data
    chapterPanelData.isMinimized = isMinimized;
    if (chapterPanelData.isMinimized) {
      chapterPanelData.panelSize = [FlatModels.PanelDesignModels.panelCollapseSize, 100 - FlatModels.PanelDesignModels.panelCollapseSize];
    } else {
      if (override) {
        if (chapterPanelData.uncollapsedPanelSize[0] > FlatModels.PanelDesignModels.panelMinSizeBig) {
          chapterPanelData.panelSize = [chapterPanelData.uncollapsedPanelSize[0], 100 - chapterPanelData.uncollapsedPanelSize[0]];
        } else {
          chapterPanelData.panelSize = [FlatModels.PanelDesignModels.panelMinSizeBig, 100 - FlatModels.PanelDesignModels.panelMinSizeBig];
          chapterPanelData.uncollapsedPanelSize = [FlatModels.PanelDesignModels.panelMinSizeBig, 100 - FlatModels.PanelDesignModels.panelMinSizeBig];
        }
      }
    }

    // Patch State
    patchState({
      chapterPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetLayerPanelSize)
  setLayerPanelSize({getState, patchState}: StateContext<SlideAuthorStateModel>,
                    {sizes}: FlatActions.SlideAuthorActions.SetLayerPanelSize) {
    // Get Layer Panel Data
    const layerPanelData = structuredClone(getState().layerPanelData);

    // Update Layer Panel Data
    layerPanelData.panelSize = sizes;
    layerPanelData.uncollapsedPanelSize = sizes;

    // Patch State
    patchState({
      layerPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.CollapseLayer)
  collapseLayer({getState, patchState}: StateContext<SlideAuthorStateModel>,
                {isMinimized, override}: FlatActions.SlideAuthorActions.CollapseLayer) {
    // Get Layer Panel Data
    const layerPanelData = structuredClone(getState().layerPanelData);

    // Update Layer Panel Data
    layerPanelData.isMinimized = isMinimized;
    if (layerPanelData.isMinimized) {
      layerPanelData.panelSize = [100 - FlatModels.PanelDesignModels.panelCollapseSize, FlatModels.PanelDesignModels.panelCollapseSize];
    } else {
      /*
      if (override) {
        if (layerPanelData.uncollapsedPanelSize[0] > FlatModels.PanelDesignModels.panelMinSizeVertical) {
          layerPanelData.panelSize = [layerPanelData.uncollapsedPanelSize[0], 100 - layerPanelData.uncollapsedPanelSize[0]];
        } else {
          layerPanelData.panelSize = [100 - FlatModels.PanelDesignModels.panelMinSizeVertical, FlatModels.PanelDesignModels.panelMinSizeVertical];
          layerPanelData.uncollapsedPanelSize = [100 - FlatModels.PanelDesignModels.panelMinSizeVertical, FlatModels.PanelDesignModels.panelMinSizeVertical];
        }
      }
      */
    }

    // Patch State
    patchState({
      layerPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetWysiwygPanelSize)
  setWysiwygPanelSize({getState, patchState}: StateContext<SlideAuthorStateModel>,
                      {sizes}: FlatActions.SlideAuthorActions.SetWysiwygPanelSize) {
    // Get Wysiwyg Panel Data
    const wysiwygPanelData = structuredClone(getState().wysiwygPanelData);

    // Update Wysiwyg Panel Data
    wysiwygPanelData.panelSize = sizes;
    wysiwygPanelData.uncollapsedPanelSize = sizes;

    // Patch State
    patchState({
      wysiwygPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.CollapseWysiwyg)
  collapseWysiwyg({getState, patchState}: StateContext<SlideAuthorStateModel>,
                  {isMinimized, override}: FlatActions.SlideAuthorActions.CollapseWysiwyg) {
    // Get Wysiwyg Panel Data
    const wysiwygPanelData = structuredClone(getState().wysiwygPanelData);

    // Update Wysiwyg Panel Data
    wysiwygPanelData.isMinimized = isMinimized;
    if (wysiwygPanelData.isMinimized) {
      wysiwygPanelData.panelSize = [FlatModels.PanelDesignModels.panelCollapseSize, 100 - FlatModels.PanelDesignModels.panelCollapseSize];
    } else {
      if (override) {
        if (wysiwygPanelData.uncollapsedPanelSize[0] > FlatModels.PanelDesignModels.panelMinSizeSmall) {
          wysiwygPanelData.panelSize = [wysiwygPanelData.uncollapsedPanelSize[0], 100 - wysiwygPanelData.uncollapsedPanelSize[0]];
        } else {
          wysiwygPanelData.panelSize = [FlatModels.PanelDesignModels.panelMinSizeSmall, 100 - FlatModels.PanelDesignModels.panelMinSizeSmall];
          wysiwygPanelData.uncollapsedPanelSize = [FlatModels.PanelDesignModels.panelMinSizeSmall, 100 - FlatModels.PanelDesignModels.panelMinSizeSmall];
        }
      }
    }

    // Patch State
    patchState({
      wysiwygPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetEditPanelSize)
  setEditPanelSize({getState, patchState}: StateContext<SlideAuthorStateModel>,
                   {sizes}: FlatActions.SlideAuthorActions.SetEditPanelSize) {
    // Get Edit Panel Data
    const editPanelData = structuredClone(getState().editPanelData);

    // Update Edit Panel Data
    editPanelData.panelSize = sizes;
    editPanelData.uncollapsedPanelSize = sizes;

    // Patch State
    patchState({
      editPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.CollapseEdit)
  collapseEdit({getState, patchState}: StateContext<SlideAuthorStateModel>,
               {isMinimized, override}: FlatActions.SlideAuthorActions.CollapseEdit) {
    // Get Edit Panel Data
    const editPanelData = structuredClone(getState().editPanelData);

    // Update Edit Panel Data
    editPanelData.isMinimized = isMinimized;
    if (editPanelData.isMinimized) {
      editPanelData.panelSize = [100 - FlatModels.PanelDesignModels.panelCollapseSize, FlatModels.PanelDesignModels.panelCollapseSize];
    } else {
      if (override) {
        if (editPanelData.uncollapsedPanelSize && editPanelData.uncollapsedPanelSize[1] && editPanelData.uncollapsedPanelSize[1] > FlatModels.PanelDesignModels.panelMinSizeSmall) {
          editPanelData.panelSize = [100 - editPanelData.uncollapsedPanelSize[1], editPanelData.uncollapsedPanelSize[1]];
        } else {
          editPanelData.panelSize = [100 - FlatModels.PanelDesignModels.panelMinSizeSmall, FlatModels.PanelDesignModels.panelMinSizeSmall];
          editPanelData.uncollapsedPanelSize = [100 - FlatModels.PanelDesignModels.panelMinSizeSmall, FlatModels.PanelDesignModels.panelMinSizeSmall];
        }
      }
    }

    // Patch State
    patchState({
      editPanelData
    });
  }

  @Action(FlatActions.SlideAuthorActions.AddTypedNodeExpanded)
  addTypedNodeExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                       {nodeId}: FlatActions.SlideAuthorActions.AddTypedNodeExpanded) {
    // Get Data
    const typedNodeExpanded = structuredClone(getState().typedNodeExpanded);
    
    // Add Node
    typedNodeExpanded[nodeId] = true;
    
    // Patch State
    patchState({
      typedNodeExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedNodeExpanded)
  setTypedNodeExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                       {typedNodeExpanded}: FlatActions.SlideAuthorActions.SetTypedNodeExpanded) {
    // Patch State
    patchState({
      typedNodeExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedPageExpanded)
  addTypedPageExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                       {pageId}: FlatActions.SlideAuthorActions.AddTypedPageExpanded) {
    // Get Data
    const typedPageExpanded = structuredClone(getState().typedPageExpanded);
    
    // Add Page
    typedPageExpanded[pageId] = true;
    
    // Patch State
    patchState({
      typedPageExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedPageExpanded)
  setTypedPageExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                       {typedPageExpanded}: FlatActions.SlideAuthorActions.SetTypedPageExpanded) {
    // Patch State
    patchState({
      typedPageExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded)
  addTypedSlideLayersExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                              {pageId}: FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded) {
    // Get Data
    const typedSlideLayersExpanded = structuredClone(getState().typedSlideLayersExpanded);
    
    // Add Page
    typedSlideLayersExpanded[pageId] = true;
    
    // Patch State
    patchState({
      typedSlideLayersExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedSlideLayersExpanded)
  setTypedSlideLayersExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                              {typedSlideLayersExpanded}: FlatActions.SlideAuthorActions.SetTypedSlideLayersExpanded) {
    // Patch State
    patchState({
      typedSlideLayersExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedSlideAnnotationsExpanded)
  addTypedSlideAnnotationsExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                                   {pageId}: FlatActions.SlideAuthorActions.AddTypedSlideAnnotationsExpanded) {
    // Get Data
    const typedSlideAnnotationsExpanded = structuredClone(getState().typedSlideAnnotationsExpanded);
    
    // Add Page
    typedSlideAnnotationsExpanded[pageId] = true;
    
    // Patch State
    patchState({
      typedSlideAnnotationsExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedSlideAnnotationsExpanded)
  setTypedSlideAnnotationsExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                                   {typedSlideAnnotationsExpanded}: FlatActions.SlideAuthorActions.SetTypedSlideAnnotationsExpanded) {
    // Patch State
    patchState({
      typedSlideAnnotationsExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedSlideGroupsExpanded)
  addTypedSlideGroupsExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                              {pageId}: FlatActions.SlideAuthorActions.AddTypedSlideGroupsExpanded) {
    // Get Data
    const typedGroupExpanded = structuredClone(getState().typedGroupExpanded);
    
    // Add Page
    typedGroupExpanded[pageId] = true;
    
    // Patch State
    patchState({
      typedGroupExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedSlideGroupsExpanded)
  setTypedSlideGroupsExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                              {typedSlideGroupsExpanded}: FlatActions.SlideAuthorActions.SetTypedSlideGroupsExpanded) {
    // Patch State
    patchState({
      typedSlideGroupsExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedSlidePoisExpanded)
  addTypedSlidePoisExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                            {pageId}: FlatActions.SlideAuthorActions.AddTypedSlidePoisExpanded) {
    // Get Data
    const typedSlidePoisExpanded = structuredClone(getState().typedSlidePoisExpanded);
    
    // Add Page
    typedSlidePoisExpanded[pageId] = true;
    
    // Patch State
    patchState({
      typedSlidePoisExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedSlidePoisExpanded)
  setTypedSlidePoisExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                            {typedSlidePoisExpanded}: FlatActions.SlideAuthorActions.SetTypedSlidePoisExpanded) {
    // Patch State
    patchState({
      typedSlidePoisExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedSlideQuestionsExpanded)
  addTypedSlideQuestionsExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                                 {pageId}: FlatActions.SlideAuthorActions.AddTypedSlideQuestionsExpanded) {
    // Get Data
    const typedSlideQuestionsExpanded = structuredClone(getState().typedSlideQuestionsExpanded);
    
    // Add Page
    typedSlideQuestionsExpanded[pageId] = true;
    
    // Patch State
    patchState({
      typedSlideQuestionsExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedSlideQuestionsExpanded)
  setTypedSlideQuestionsExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                                 {typedSlideQuestionsExpanded}: FlatActions.SlideAuthorActions.SetTypedSlideQuestionsExpanded) {
    // Patch State
    patchState({
      typedSlideQuestionsExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedLayerExpanded)
  addTypedLayerExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                        {layerId}: FlatActions.SlideAuthorActions.AddTypedLayerExpanded) {
    // Get Data
    const typedLayerExpanded = structuredClone(getState().typedLayerExpanded);
    
    // Add Layer
    typedLayerExpanded[layerId] = true;
    
    // Patch State
    patchState({
      typedLayerExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedLayerExpanded)
  setTypedLayerExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                        {typedLayerExpanded}: FlatActions.SlideAuthorActions.SetTypedLayerExpanded) {
    // Patch State
    patchState({
      typedLayerExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedImageExpanded)
  addTypedImageExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                        {imageId}: FlatActions.SlideAuthorActions.AddTypedImageExpanded) {
    // Get Data
    const typedImageExpanded = structuredClone(getState().typedImageExpanded);
    
    // Add Image
    typedImageExpanded[imageId] = true;
    
    // Patch State
    patchState({
      typedImageExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedImageExpanded)
  setTypedImageExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                        {typedImageExpanded}: FlatActions.SlideAuthorActions.SetTypedImageExpanded) {
    // Patch State
    patchState({
      typedImageExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedImageAnnotationExpanded)
  addTypedImageAnnotationExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                                  {annotationId}: FlatActions.SlideAuthorActions.AddTypedImageAnnotationExpanded) {
    // Get Data
    const typedImageAnnotationExpanded = structuredClone(getState().typedImageAnnotationExpanded);
    
    // Add Annotation
    typedImageAnnotationExpanded[annotationId] = true;
    
    // Patch State
    patchState({
      typedImageAnnotationExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedImageAnnotationExpanded)
  setTypedImageAnnotationExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                                  {typedImageAnnotationExpanded}: FlatActions.SlideAuthorActions.SetTypedImageAnnotationExpanded) {
    // Patch State
    patchState({
      typedImageAnnotationExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedImagePoiExpanded)
  addTypedImagePoiExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                           {poiId}: FlatActions.SlideAuthorActions.AddTypedImagePoiExpanded) {
    // Get Data
    const typedImagePoiExpanded = structuredClone(getState().typedImagePoiExpanded);
    
    // Add Poi
    typedImagePoiExpanded[poiId] = true;
    
    // Patch State
    patchState({
      typedImagePoiExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedImagePoiExpanded)
  setTypedImagePoiExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                           {typedImagePoiExpanded}: FlatActions.SlideAuthorActions.SetTypedImagePoiExpanded) {
    // Patch State
    patchState({
      typedImagePoiExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedGroupExpanded)
  addTypedGroupExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                        {groupId}: FlatActions.SlideAuthorActions.AddTypedGroupExpanded) {
    // Get Data
    const typedGroupExpanded = structuredClone(getState().typedGroupExpanded);
    
    // Add Group
    typedGroupExpanded[groupId] = true;
    
    // Patch State
    patchState({
      typedGroupExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedGroupExpanded)
  setTypedGroupExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                        {typedGroupExpanded}: FlatActions.SlideAuthorActions.SetTypedGroupExpanded) {
    // Patch State
    patchState({
      typedGroupExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedPoiExpanded)
  addTypedPoiExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                      {poiId}: FlatActions.SlideAuthorActions.AddTypedPoiExpanded) {
    // Get Data
    const typedPoiExpanded = structuredClone(getState().typedPoiExpanded);
    
    // Add Poi
    typedPoiExpanded[poiId] = true;
    
    // Patch State
    patchState({
      typedPoiExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedPoiExpanded)
  setTypedPoiExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                      {typedPoiExpanded}: FlatActions.SlideAuthorActions.SetTypedPoiExpanded) {
    // Patch State
    patchState({
      typedPoiExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedPoiAnnotationExpanded)
  addTypedPoiAnnotationExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                                {annotationId}: FlatActions.SlideAuthorActions.AddTypedPoiAnnotationExpanded) {
    // Get Data
    const typedPoiAnnotationExpanded = structuredClone(getState().typedPoiAnnotationExpanded);
    
    // Add Annotation
    typedPoiAnnotationExpanded[annotationId] = true;
    
    // Patch State
    patchState({
      typedPoiAnnotationExpanded
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetTypedPoiAnnotationExpanded)
  setTypedPoiAnnotationExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                                {typedPoiAnnotationExpanded}: FlatActions.SlideAuthorActions.SetTypedPoiAnnotationExpanded) {
    // Patch State
    patchState({
      typedPoiAnnotationExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.AddTypedQuestionExpanded)
  addTypedQuestionExpanded({getState, patchState}: StateContext<SlideAuthorStateModel>,
                           {questionId}: FlatActions.SlideAuthorActions.AddTypedQuestionExpanded) {
    // Get Data
    const typedQuestionExpanded = structuredClone(getState().typedQuestionExpanded);
    
    // Add Question
    typedQuestionExpanded[questionId] = true;
    
    // Patch State
    patchState({
      typedQuestionExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.SetTypedQuestionExpanded)
  setTypedQuestionExpanded({patchState}: StateContext<SlideAuthorStateModel>,
                           {typedQuestionExpanded}: FlatActions.SlideAuthorActions.SetTypedQuestionExpanded) {
    // Patch State
    patchState({
      typedQuestionExpanded
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.SetEditPanelOverlay)
  setEditPanelOverlay({patchState}: StateContext<SlideAuthorStateModel>,
                      {editPanelOverlay}: FlatActions.SlideAuthorActions.SetEditPanelOverlay) {
    // Patch State
    patchState({
      editPanelOverlay
    });
  }

  @Action(FlatActions.SlideAuthorActions.SetEditPanelDisplay)
  setEditPanelDisplay({patchState}: StateContext<SlideAuthorStateModel>,
                      {editPanelDisplay, editPanelBulkEdit}: FlatActions.SlideAuthorActions.SetEditPanelDisplay) {
    // Patch State
    patchState({
      editPanelDisplay,
      editPanelBulkEdit
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.SetMainPanelDisplay)
  setMainPanelDisplay({patchState}: StateContext<SlideAuthorStateModel>,
                      {mainPanelDisplay}: FlatActions.SlideAuthorActions.SetMainPanelDisplay) {
    // Patch State
    patchState({
      mainPanelDisplay
    });
  }
  
  @Action(FlatActions.SlideAuthorActions.SetIsSplitterMoving)
  setIsSplitterMoving({patchState}: StateContext<SlideAuthorStateModel>,
                      {isSplitterMoving}: FlatActions.SlideAuthorActions.SetIsSplitterMoving) {
    // Patch State
    patchState({
      isSplitterMoving
    });
  }

  //</editor-fold>
}
